import _ from 'lodash';
import moment from 'moment';
import { ReportingTeamUser } from '../Types';
import {
  Period,
  PeriodType,
  WeeklyPeriod,
  MonthlyPeriod,
  GetCoachingStatsResponse,
  getCalendarMonth,
  getAmazonWeeksOfCurrentMonth,
  getAmazonWeeksOfLastMonth,
} from '@amzn/aet-coachingstats-kit';
import {
  TMCoachingStatsSummaryForOM,
  OMCoachingStatsSummary,
  OMLevelStats,
  BasicStats,
  BasicStatsForOM,
  TMInfoForOM,
  OMWeeklyCoachingCompletionPercentage,
} from '../Types/CoachingStats';
import { MonthSelection } from '../PerformancePortal/OMCoaching/types';
import { getTMCoachingStatsSummaryInternal } from './CoachingStatsUtilsForTM';

export const aggregateBasicStatsForOM = (
  basicStatsList: BasicStats[]
): BasicStatsForOM => {
  const aggregatedStatsForOM = {
    totalCoachingRequired: 0,
    remainingCoachings: 0,
    completedCoachings: 0,

    totalContactReviews: 0,
  };

  basicStatsList.forEach(basicStats => {
    aggregatedStatsForOM.totalCoachingRequired +=
      basicStats.totalCoachingRequired;
    aggregatedStatsForOM.remainingCoachings += basicStats.remainingCoachings;
    aggregatedStatsForOM.completedCoachings += basicStats.completedCoachings;

    aggregatedStatsForOM.totalContactReviews += basicStats.totalContactReviews;
  });

  return aggregatedStatsForOM;
};

export const calculateCoachingCompletionPercentage = (
  tmStats: BasicStatsForOM[]
): number => {
  if (!tmStats) return 0; // To facilitate calculation of weekly coaching rate until we find a better solution

  const allCompletedCoachings = _.chain(tmStats)
    .map(singleTMStats => singleTMStats.completedCoachings)
    .sum()
    .value();
  const allRequiredCoachings = _.chain(tmStats)
    .map(singleTMStats => singleTMStats.totalCoachingRequired)
    .sum()
    .value();
  const coachingCompletionPercentage =
    allRequiredCoachings == 0 // intentionally no type checking
      ? 0
      : (allCompletedCoachings / allRequiredCoachings) * 100;

  return Math.floor(coachingCompletionPercentage);
};

export const calculateOMLevelStats = (
  periods: Period[],
  tmStats: TMCoachingStatsSummaryForOM[]
): OMLevelStats => {
  const allMonthlyCoachings = tmStats.flatMap(
    singleTMStats => singleTMStats.statsInfo.monthlyCoachingStatsSummary
  );

  const allWeeklyCoachings = tmStats.map(
    singleTMStats => singleTMStats.statsInfo.weeklyCoachingStatsSummaries
  );

  const monthlyCoachingCompletionPercentage =
    calculateCoachingCompletionPercentage(allMonthlyCoachings);

  // Transposing the multi-dimentional array (matrix)
  const weeklyGroupedCoachings = _.zip(...allWeeklyCoachings);

  const weeklyCoachingCompletionPercentageNumbers = weeklyGroupedCoachings.map(
    weeklyGroup => {
      // casting to convince the typescript that all weekly tmStats for each TM was of the same size
      // as per the business logic implemented outside of tthe scope of this method.
      return calculateCoachingCompletionPercentage(
        weeklyGroup as BasicStatsForOM[]
      );
    }
  );

  const weeklyNumbers = periods
    .filter(period => period.periodType === PeriodType.WEEK)
    .map(period => (period as WeeklyPeriod).week); // we already filtered WeeklyPeriods just one step above

  const weeklyCoachingCompletionPercentages: OMWeeklyCoachingCompletionPercentage[] =
    _.zipWith(
      weeklyNumbers,
      weeklyCoachingCompletionPercentageNumbers,
      (weekNum, percentage) => ({
        weekNum,
        percentage,
      })
    );

  return {
    weeklyCoachingCompletionPercentages,
    monthlyCoachingCompletionPercentage,
  };
};
export const getOMCoachingStatsSummary = (
  periods: Period[],
  batchCoachingStats: GetCoachingStatsResponse[],
  tmDirectReportsOfOMMap: Record<string, ReportingTeamUser>
): OMCoachingStatsSummary => {
  const tmStats = batchCoachingStats.map(coachingStatsForSingleTM => {
    const coachingStatsByPeriodMap = _.chain(
      coachingStatsForSingleTM.coachingStatsForPeriods
    )
      .groupBy(
        coachingStatsForPeriods => coachingStatsForPeriods.period.periodType
      )
      .value();

    const tmUserId = coachingStatsForSingleTM.userId;

    const { relatedUser, subReports } = tmDirectReportsOfOMMap[tmUserId];

    const tmInfo: TMInfoForOM = {
      userId: tmUserId,
      fullName: relatedUser.displayName,
      alias: relatedUser.alias,
      directReportCount: subReports ? subReports.length : 0,
      subReports: subReports ?? [],
    };

    const monthlyCoachingStats = _.first(
      coachingStatsByPeriodMap[PeriodType.MONTH]
    )!; // there is 1 and only 1 monthly coaching stats

    const weeklyCoachingStatList = coachingStatsByPeriodMap[
      PeriodType.WEEK
    ].sort((a, b) =>
      sortWeeklyPeriods(a.period as WeeklyPeriod, b.period as WeeklyPeriod)
    );

    const coachingStatsSummaryForSingleTM = getTMCoachingStatsSummaryInternal(
      weeklyCoachingStatList,
      monthlyCoachingStats
    );

    // Calculate stats aggregated across Week for each TM, to show in each TM row and TM profile.
    const aggregatedWeeklyCoachingStatsSummaryForSingleTM =
      aggregateBasicStatsForOM(
        coachingStatsSummaryForSingleTM.extendedWeeklyCoachingSummaries.flatMap(
          coachingStatsSummary =>
            coachingStatsSummary.basicStats.allAssociateTypes
        )
      );

    const aggregatedNewHireCoachingStatsSummaryForSingleTM =
      aggregateBasicStatsForOM(
        coachingStatsSummaryForSingleTM.extendedWeeklyCoachingSummaries.flatMap(
          coachingStatsSummary => coachingStatsSummary.basicStats.newHire
        )
      );

    const aggregatedBottomQuartileCoachingStatsSummaryForSingleTM =
      aggregateBasicStatsForOM(
        coachingStatsSummaryForSingleTM.extendedWeeklyCoachingSummaries.flatMap(
          coachingStatsSummary => coachingStatsSummary.basicStats.bottomQuartile
        )
      );

    // Calculate the monthly and weekly stats to show as OM level statistics after aggregating across TM.
    const monthlyCoachingStatsSummary: BasicStatsForOM =
      coachingStatsSummaryForSingleTM.extendedMonthlyCoachingSummary.basicStats
        .allAssociateTypes;

    const weeklyCoachingStatsSummaries: BasicStatsForOM[] =
      coachingStatsSummaryForSingleTM.extendedWeeklyCoachingSummaries.flatMap(
        coachingStatsSummary =>
          coachingStatsSummary.basicStats.allAssociateTypes
      );

    const tmCoachingStatsInfo: TMCoachingStatsSummaryForOM = {
      tmInfo,
      statsInfo: {
        monthlyCoachingStatsSummary,
        weeklyCoachingStatsSummaries,
        aggregatedWeeklyCoachingStatsSummary:
          aggregatedWeeklyCoachingStatsSummaryForSingleTM,
        aggregatedNewHireCoachingStatsSummary:
          aggregatedNewHireCoachingStatsSummaryForSingleTM,
        aggregatedBottomQuartileCoachingStatsSummary:
          aggregatedBottomQuartileCoachingStatsSummaryForSingleTM,
      },
    };

    return tmCoachingStatsInfo;
  });

  const omStats = calculateOMLevelStats(periods, tmStats);

  return {
    omStats, // For OMLevelCoachingCompletionRates
    tmStats, // For TmTable
  };
};

// TODO: We may consider moving this method to AETUtils
export const getOMPeriodsForNow = (monthSelection: MonthSelection) => {
  const thisMonthPeriod: MonthlyPeriod = {
    periodType: PeriodType.MONTH,
    year: getCalendarMonth(moment().toISOString()).year,
    month: getCalendarMonth(moment().toISOString()).month,
  };

  const lastMonthPeriod: MonthlyPeriod = {
    periodType: PeriodType.MONTH,
    year: getCalendarMonth(moment().subtract(1, 'month').toISOString()).year,
    month: getCalendarMonth(moment().subtract(1, 'month').toISOString()).month,
  };

  const periods =
    monthSelection === MonthSelection.THIS_MONTH
      ? [thisMonthPeriod, ...getAmazonWeeksOfCurrentMonth()]
      : [lastMonthPeriod, ...getAmazonWeeksOfLastMonth()];

  return periods;
};

export const sortWeeklyPeriods = (
  weeklyPeriodA: WeeklyPeriod,
  weeklyPeriodB: WeeklyPeriod
) => {
  return weeklyPeriodA.year === weeklyPeriodB.year
    ? weeklyPeriodA.week - weeklyPeriodB.week
    : weeklyPeriodA.year - weeklyPeriodB.year;
};

export const getCoachingStatsSummaryForTm = (
  omCoachingStatsSummary: OMCoachingStatsSummary,
  tmUserId: string
): TMCoachingStatsSummaryForOM => {
  const tmStats = omCoachingStatsSummary.tmStats.find(
    tmStat => tmStat.tmInfo.userId === tmUserId
  );

  if (!tmStats) {
    throw new Error(
      `TM with userId ${tmUserId} not found in coaching stats summary`
    );
  }

  return tmStats;
};
